import StrategyType from 'enums/modules/strategies/strategyType';
import queryString from 'query-string';
import { CreatePeCompanyOrderRouterParams } from './params/pe';
import { FreedomType } from 'enums/shared/brokerType';

export default abstract class AppRoute {
  static login = '/login';

  static portfolio = '/portfolio';

  // SCOR
  static scor = '/scor';
  static scorOrder = `${AppRoute.scor}/order`;
  static scorReports = `${AppRoute.scor}/reports`;
  static scorReport = (id: ID): string => `${AppRoute.scorReports}/${id}`;
  static scorReportPath = `${AppRoute.scorReports}/:id`;
  static scorPool = `${AppRoute.scor}/pool`;
  static scorPoolDetail = (id: ID): string => `${AppRoute.scorPool}/${id}`;
  static scorPoolDetailPath = `${AppRoute.scorPool}/:id`;
  static scorShared = `${AppRoute.scor}/shared`;
  static scorSharedView = (token: string): string => `${AppRoute.scorShared}/${token}`;
  static scorSharedViewPath = `${AppRoute.scorShared}/:token`;
  static scorFind = `${AppRoute.scor}/find`;
  static scorInvites = `${AppRoute.scor}/invites`;
  static scorShare = `${AppRoute.scor}/share`;
  static scorReferral = `${AppRoute.scor}/referral`;
  static scorSubscription = `${AppRoute.scor}/subscription`;
  static scorInvoices = `${AppRoute.scor}/invoices`;
  static scorInvoice = (id: ID): string => `${AppRoute.scorInvoices}/${id}`;
  static scorInvoicePath = `${AppRoute.scorInvoices}/:id`;
  static scorFaq = `${AppRoute.scor}/faq`;
  static scorDocuments = `${AppRoute.scor}/documents`;
  static operationsPath = `${AppRoute.portfolio}/operations`;
  static operations = (queryParams: OperationsRouterParams): string =>
    `${AppRoute.portfolio}/operations?${queryString.stringify(queryParams)}`;

  static dealsHistoryPath = `${AppRoute.portfolio}/deals-history`;
  static dealsHistory = (queryParams: OperationsRouterParams): string =>
    `${AppRoute.dealsHistoryPath}?${queryString.stringify(queryParams)}`;

  static createWithdrawalOrderPath =
    '/portfolio/withdrawal/order/create/:accountId';

  static createWithdrawalOrder = (accountId: ID): string =>
    `/portfolio/withdrawal/order/create/${accountId}`;

  static withdrawalOrdersPath = '/portfolio/withdrawal/orders';

  static withdrawalOrders = (accountId: ID): string =>
    `${AppRoute.withdrawalOrdersPath}?account_id=${accountId}`;

  static createReport = `${AppRoute.portfolio}/report/create`;

  static createReportDetail = (id: ID): string =>
    `${AppRoute.createReport}/${id}`;

  static createReportDetailDetailPath = `${AppRoute.createReport}/:id`;

  // PE
  static pe = '/pe';
  static peOffers = `${AppRoute.pe}/offers`;

  static strategies = `${AppRoute.pe}/strategies`;
  static strategiesDetail = (id: StrategyType): string =>
    `${AppRoute.strategies}/${id}`;

  static strategiesDetailPath = `${AppRoute.strategies}/:id`;

  static peDeals = `${AppRoute.pe}/my-actives`;
  static peDealDetail = (id: ID): string => `${AppRoute.peDeals}/${id}`;
  static peDealDetailPath = `${AppRoute.peDeals}/:id`;

  static peCompanyOrderDetail = (id: ID): string =>
    `${AppRoute.pe}/company-orders/${id}`;

  static peCompanyOrderDetailPath = `${AppRoute.pe}/company-orders/:id`;

  static peP2POrderDetail = (id: ID): string =>
    `${AppRoute.pe}/p2p-orders/${id}`;

  static peP2POrderDetailPath = `${AppRoute.pe}/p2p-orders/:id`;

  static peCompanyDetail = (id: ID): string => `${AppRoute.pe}/company/${id}`;
  static peCompanyDetailPath = `${AppRoute.pe}/company/:id`;

  static peSelfOrders = `${AppRoute.pe}/my-orders`;
  static peSelfOrderDetail = (id: ID): string =>
    `${AppRoute.peSelfOrders}/${id}`;

  static peSelfOrderDetailPath = `${AppRoute.peSelfOrders}/:id`;

  static createPeOrder = `${AppRoute.pe}/create-order`;

  static createPeCompanyOrderPath = `${AppRoute.pe}/create-company-order/:companyId`;
  static createPeCompanyOrder = (
    companyId: ID,
    params: CreatePeCompanyOrderRouterParams
  ): string =>
    `${AppRoute.pe}/create-company-order/${companyId}?${queryString.stringify(
      params
    )}`;

  // Chat
  static chatList = '/chat';
  static chatDetail = (id: number): string => `/chat/${id}`;
  static chatDetailPath = '/chat/:id';

  // Orders
  static recommendations = `${AppRoute.pe}/recommendations`;
  static recommendationDetail = (id: number): string =>
    `${AppRoute.recommendations}/${id}`;

  static recommendationDetailPath = `${AppRoute.recommendations}/:id`;

  static recommendationsWithTab = (tab: string): string =>
    `${AppRoute.recommendations}?tab=${tab}`;

  // Settings
  static settings = '/settings';

  // Profile
  static profile = `${AppRoute.settings}/profile`;
  static createBrokerAccount = `${AppRoute.settings}/broker/create`;
  static brokerList = `${AppRoute.settings}/broker`;
  static compensationList = `${AppRoute.settings}/compensation`;
  static notifications = `${AppRoute.settings}/notifications`;
  static notificationDetail = (id: string): string => `${AppRoute.notifications}/${id}`;
  static notificationDetailPath = `${AppRoute.notifications}/:id`;

  // Connect broker account
  static connectTradernetBroker = `${AppRoute.createBrokerAccount}/tradernet`;
  static connectFinamBroker = `${AppRoute.createBrokerAccount}/finam`;
  static connectBinanceBroker = `${AppRoute.createBrokerAccount}/binance`;
  static connectTinkoffBroker = `${AppRoute.createBrokerAccount}/tinkoff`;
  static connectFreedomBrokerPath = `${AppRoute.createBrokerAccount}/freedom/:freedomType`;
  static connectFreedomBroker = (freedomType: FreedomType | null): string =>
    `${AppRoute.createBrokerAccount}/freedom/${freedomType?.toString()}`;
  // Home

  static home = '/';
  static selfIpo = '/home/self-ipo';
  static selfIpoApplications = `${AppRoute.selfIpo}/applications`;
  static selfIpoDetail = (id: ID): string => `${AppRoute.selfIpo}/${id}`;
  static selfIpoDetailPath = `${AppRoute.selfIpo}/:id`;

  static serviceProducts = '/services/products';

  static serviceProductDetail = (id: number): string =>
    `${AppRoute.serviceProducts}/${id}`;

  static serviceProductDetailPath = `${AppRoute.serviceProducts}/:id`;

  // Online-Documents
  static myDocumentsPath = `${AppRoute.settings}/myDocuments`;
  static documentDetailPath = `${AppRoute.myDocumentsPath}/detailDocument/:id`;

  static documentDetail = (documentId: ID): string =>
    `${AppRoute.myDocumentsPath}/detailDocument/${documentId}`;

  // Telegram
  static telegramPath = `${AppRoute.settings}/telegram`;
  // Surveys
  static generalSurvey = '/survey/general';

  static communityPath = '/community';
  static privateChannel = `${AppRoute.communityPath}/private-channel`;
  static privateChannelArticleCommentsPath = `${AppRoute.privateChannel}/:id/:isCommentable/comments`;
  static privateChannelArticleComments = (
    articleId: ID,
    isCommentable: boolean
  ): string =>
    `${AppRoute.privateChannel}/${articleId}/${isCommentable}/comments`;

  // AI
  static aiChatDetail = (id: string): string => `${AppRoute.aiPath}/${id}`;
  static aiPath = '/ai';
  // Events
  static eventsPath = `${AppRoute.communityPath}/events`;
  static events = (categoryId?: ID): string => {
    let path = AppRoute.eventsPath;
    if (categoryId) {
      path += `?category=${categoryId}`;
    }

    return path;
  };

  static eventDetail = (id: number): string => `${AppRoute.eventsPath}/${id}`;
  static eventDetailPath = `${AppRoute.eventsPath}/:id`;
}
